import React, { useState } from 'react';
// Company creation form - cleaned imports
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { logger } from '../../utils/logger';

interface CreateCompanyFormData {
  name: string;
  email_domain: string;
  tier: 'FREE' | 'BASIC' | 'PROFESSIONAL' | 'ENTERPRISE';
  admin_email: string;
  admin_first_name: string;
  admin_last_name: string;
}

interface CompanyResponse {
  company_id: string;
  company_name: string;
  company_tier: string;
  admin_user_id: string;
  admin_email: string;
  created_at: string;
}

const CreateCompany: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<CreateCompanyFormData>({
    name: '',
    email_domain: '',
    tier: 'FREE',
    admin_email: '',
    admin_first_name: '',
    admin_last_name: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [createdCompany, setCreatedCompany] = useState<CompanyResponse | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTierChange = (e: any) => {
    setFormData((prev) => ({
      ...prev,
      tier: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await api.post<CompanyResponse>('/admin/companies', formData);

      setCreatedCompany(response.data);
      setSuccess(true);

      // Reset form and redirect after 2 seconds
      setFormData({
        name: '',
        email_domain: '',
        tier: 'FREE',
        admin_email: '',
        admin_first_name: '',
        admin_last_name: '',
      });

      setTimeout(() => {
        navigate('/admin/companies');
      }, 2000);
    } catch (err: any) {
      logger.error('Create company error:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to create company';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
        <Typography variant="h4" component="h1">
          Kreiraj Novu Kompaniju
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && createdCompany && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <strong>Uspešno!</strong> Kompanija <strong>{createdCompany.company_name}</strong> je kreirana.
          <br />
          Administrator može generisati API ključ u sekciji "Moj nalog" nakon prijave.
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Informacije o Kompaniji
            </Typography>

            <TextField
              fullWidth
              label="Naziv Kompanije"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              margin="normal"
              placeholder="npr. Acme Corporation"
            />

            <TextField
              fullWidth
              label="Email Domen"
              name="email_domain"
              value={formData.email_domain}
              onChange={handleInputChange}
              margin="normal"
              placeholder="npr. @acme.com"
              helperText="Opciono - ograniči korisnike na ovaj domen"
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Paket</InputLabel>
              <Select
                value={formData.tier}
                onChange={handleTierChange}
                label="Paket"
              >
                <MenuItem value="FREE">Besplatno (Ograničene funkcije)</MenuItem>
                <MenuItem value="BASIC">Osnovno ($49/mesečno)</MenuItem>
                <MenuItem value="PROFESSIONAL">Profesionalno ($149/mesečno)</MenuItem>
                <MenuItem value="ENTERPRISE">Korporativno (Prilagođena cena)</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Administrator Korisnik
            </Typography>

            <TextField
              fullWidth
              label="Email Administratora"
              name="admin_email"
              type="email"
              value={formData.admin_email}
              onChange={handleInputChange}
              required
              margin="normal"
              placeholder="admin@acme.com"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Ime"
                name="admin_first_name"
                value={formData.admin_first_name}
                onChange={handleInputChange}
                required
                margin="normal"
              />

              <TextField
                fullWidth
                label="Prezime"
                name="admin_last_name"
                value={formData.admin_last_name}
                onChange={handleInputChange}
                required
                margin="normal"
              />
            </Box>

            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
                fullWidth
              >
                {loading ? 'Kreiram...' : 'Kreiraj Kompaniju'}
              </Button>

              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/admin/companies')}
                disabled={loading}
              >
                Otkaži
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreateCompany;
